/**
 * PWA Installation Context Manager
 * Tracks where the user installed the PWA from and treats that as their "home" domain
 * Implements Google's recommendation for multi-domain PWA installations
 */

export interface PWAInstallationContext {
  installationDomain: string;
  installationTimestamp: number;
  isMainApp: boolean;
  affiliationId?: string;
  affiliationName?: string;
}

// Use domain-specific keys to prevent conflicts when multiple PWAs are installed
const getInstallationContextKey = (): string => {
  const domain = window.location.hostname;
  // Create unique key per domain/subdomain
  const normalizedDomain = domain.replace(/[^a-z0-9]/gi, "_");
  return `zurilink_pwa_context_${normalizedDomain}`;
};

const INSTALLATION_CONTEXT_KEY = getInstallationContextKey();

export class PWAInstallationManager {
  /**
   * Record PWA installation context when user installs the app
   */
  static recordInstallation(
    options: {
      domain?: string;
      affiliationId?: string;
      affiliationName?: string;
    } = {}
  ): void {
    const currentDomain = options.domain || window.location.hostname;
    const isMainApp =
      currentDomain === "zurilink.com" ||
      currentDomain.includes("localhost:3000");

    const context: PWAInstallationContext = {
      installationDomain: currentDomain,
      installationTimestamp: Date.now(),
      isMainApp,
      affiliationId: options.affiliationId,
      affiliationName: options.affiliationName,
    };

    // Store in localStorage to persist across sessions
    localStorage.setItem(INSTALLATION_CONTEXT_KEY, JSON.stringify(context));

  }

  /**
   * Get the stored installation context
   */
  static getInstallationContext(): PWAInstallationContext | null {
    try {
      const stored = localStorage.getItem(INSTALLATION_CONTEXT_KEY);
      if (!stored) return null;

      const context = JSON.parse(stored) as PWAInstallationContext;

      // Validate the context structure
      if (!context.installationDomain || !context.installationTimestamp) {
        return null;
      }

      return context;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get the user's "home" URL based on where they installed the PWA
   */
  static getHomeUrl(): string {
    const context = this.getInstallationContext();

    if (!context) {
      // No installation context - default to current domain logic
      return this.getCurrentDomainHome();
    }

    const isLocalhost = context.installationDomain.includes("localhost");

    if (context.isMainApp) {
      if (isLocalhost) {
        return "http://localhost:3000/";
      } else {
        return "https://zurilink.com/";
      }
    } else {
      if (isLocalhost) {
        return "http://localhost:3001/";
      } else {
        return `https://${context.installationDomain}/`;
      }
    }
  }

  /**
   * Check if current domain matches the installation domain
   */
  static isOnHomeDomain(): boolean {
    const context = this.getInstallationContext();

    if (!context) {
      return true; // No context means we treat current as home
    }

    const currentDomain = window.location.hostname;
    return currentDomain === context.installationDomain;
  }

  /**
   * Get redirect URL for going "home" from any domain
   */
  static getRedirectToHomeUrl(): string {
    const homeUrl = this.getHomeUrl();
    const currentOrigin = window.location.origin;

    // If home URL is same origin, return path only
    try {
      const homeOrigin = new URL(homeUrl).origin;
      if (homeOrigin === currentOrigin) {
        return new URL(homeUrl).pathname;
      }
    } catch (error) {
    }

    // Different origin - return full URL for redirect
    return homeUrl;
  }

  /**
   * Handle PWA launch - redirect to home if needed
   */
  static handlePWALaunch(): string | null {
    const context = this.getInstallationContext();

    if (!context) {
      // No installation context - let normal routing handle it
      return null;
    }

    const currentDomain = window.location.hostname;

    // If we're on the installation domain, proceed normally
    if (currentDomain === context.installationDomain) {
      return null;
    }

    // We're on a different domain from where PWA was installed
    // Redirect to the installation domain
    return this.getHomeUrl();
  }

  /**
   * Auto-detect and record installation context when appropriate
   */
  static autoDetectInstallation(): void {
    // Only record if we don't already have a context
    if (this.getInstallationContext()) {
      return;
    }

    // Check if this looks like a PWA installation scenario
    const isPWA = this.isPWAContext();
    const isFromHomeScreen = this.isLaunchedFromHomeScreen();

    if (isPWA || isFromHomeScreen) {
      // Auto-record installation context
      const currentDomain = window.location.hostname;

      // For whitelabel domains, try to get affiliation info
      if (
        currentDomain.endsWith(".zurilink.com") &&
        currentDomain !== "zurilink.com"
      ) {
        const subdomain = currentDomain.replace(".zurilink.com", "");
        this.recordInstallation({
          affiliationId: subdomain, // Using subdomain as ID for now
          affiliationName: subdomain,
        });
      } else {
        this.recordInstallation();
      }
    }
  }

  /**
   * Clear installation context (for testing or user reset)
   */
  static clearInstallationContext(): void {
    localStorage.removeItem(INSTALLATION_CONTEXT_KEY);
  }

  /**
   * Get current domain's default home path
   */
  private static getCurrentDomainHome(): string {
    return "/";
  }

  /**
   * Check if we're in a PWA context
   */
  private static isPWAContext(): boolean {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as any).standalone === true ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      window.matchMedia("(display-mode: minimal-ui)").matches
    );
  }

  /**
   * Check if launched from home screen
   */
  private static isLaunchedFromHomeScreen(): boolean {
    return (
      document.referrer === "" &&
      window.location.search.includes("utm_source=pwa")
    );
  }

  /**
   * Update installation context with additional info (like affiliation details)
   */
  static updateInstallationContext(
    updates: Partial<PWAInstallationContext>
  ): void {
    const current = this.getInstallationContext();
    if (!current) return;

    const updated = { ...current, ...updates };
    localStorage.setItem(INSTALLATION_CONTEXT_KEY, JSON.stringify(updated));
  }
}
